<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="$emit('close')">
    <div data-tour="edit-profile-modal" class="w-full max-w-md bg-surface dark:bg-surface-dark rounded-xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">Edit Profile</h2>
        <button @click="$emit('close')" data-tour="edit-profile-close-btn" class="text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="px-6 py-2 overflow-y-auto divide-y divide-stroke-subtle dark:divide-stroke-dark">
        <p v-if="loading" class="text-sm text-fg-muted dark:text-fg-muted-dark py-4">Loading…</p>
        <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400 py-4">{{ loadError }}</p>

        <template v-else>
          <!-- Availability -->
          <div data-tour="availability-section" class="py-4">
            <div class="flex items-center justify-between mb-2">
              <label class="text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark">Availability</label>
              <button
                type="button"
                @click="editingAvailability = !editingAvailability"
                class="text-xs font-medium text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >{{ editingAvailability ? 'Done' : 'Edit' }}</button>
            </div>

            <!-- Read-only summary -->
            <template v-if="!editingAvailability">
              <div v-if="activeDays.length" class="space-y-1">
                <div
                  v-for="day in activeDays" :key="day"
                  class="flex justify-between text-sm text-fg-secondary dark:text-fg-secondary-dark"
                >
                  <span class="w-10 text-fg-muted dark:text-fg-muted-dark">{{ day.slice(0, 3) }}</span>
                  <span class="text-right">{{ formatWindows(form.availability[day]) }}</span>
                </div>
              </div>
              <p v-else class="text-sm text-fg-faint dark:text-fg-faint-dark italic">Not set</p>
            </template>

            <!-- Expanded editor -->
            <div v-else class="space-y-2">
              <div
                v-for="day in WEEKDAYS" :key="day"
                class="rounded-lg border transition-colors"
                :class="form.availability[day]
                  ? 'border-blue-500/30 bg-blue-500/[0.04] dark:bg-blue-500/[0.06]'
                  : 'border-stroke-subtle dark:border-stroke-dark'"
              >
                <div class="flex items-center gap-2.5 px-3 py-2">
                  <ToggleSwitch
                    :model-value="!!form.availability[day]"
                    @update:model-value="checked => toggleDay(day, checked)"
                    :id="`profile-avail-${day}`"
                  />
                  <label :for="`profile-avail-${day}`" class="text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark select-none flex-1">{{ day }}</label>
                  <span v-if="!form.availability[day]" class="text-xs text-fg-faint dark:text-fg-faint-dark italic">Unavailable</span>
                </div>
                <div v-if="form.availability[day]" class="px-3 pb-3 space-y-1.5">
                  <div v-for="(block, i) in form.availability[day]" :key="i" class="group flex items-center gap-2">
                    <input
                      type="time"
                      :value="block.start"
                      @change="block.start = $event.target.value"
                      class="min-w-0 flex-1 px-2 py-1 text-sm rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span class="text-sm text-fg-faint dark:text-fg-faint-dark">–</span>
                    <input
                      type="time"
                      :value="block.end"
                      @change="block.end = $event.target.value"
                      class="min-w-0 flex-1 px-2 py-1 text-sm rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      v-if="form.availability[day].length > 1"
                      type="button"
                      @click="removeBlock(day, i)"
                      class="flex-shrink-0 w-4 text-fg-faint hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove this block"
                    >
                      <XMarkIcon class="h-4 w-4" />
                    </button>
                    <span v-else class="flex-shrink-0 w-4"></span>
                  </div>
                  <button
                    type="button"
                    @click="addBlock(day)"
                    data-tour="availability-add-block"
                    class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <PlusIcon class="h-3 w-3" /> Add block
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Instruments -->
          <div class="py-4">
            <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Instruments</label>
            <input
              v-model="instrumentsText"
              type="text"
              placeholder="e.g. Guitar, Piano"
              class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p class="mt-1 text-xs text-fg-faint dark:text-fg-faint-dark">Comma-separated</p>
          </div>

          <!-- Bio -->
          <div class="py-4">
            <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Bio</label>
            <textarea
              v-model="form.bio"
              rows="4"
              placeholder="Tell families a bit about yourself…"
              class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>

          <!-- Cancellation policy -->
          <div class="py-4">
            <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Cancellation Policy</label>
            <textarea
              v-model="form.cancellation_policy"
              rows="3"
              placeholder="Your cancellation policy…"
              class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
          </div>
        </template>

        <p v-if="saveError" class="text-sm text-red-600 dark:text-red-400 py-4">{{ saveError }}</p>
        <p v-if="saveSuccess" class="text-sm text-green-600 dark:text-green-400 py-4">Profile updated.</p>
      </div>

      <div class="flex justify-end gap-3 px-6 py-4 border-t border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <button
          type="button"
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark rounded-lg transition-colors"
        >Cancel</button>
        <button
          v-if="!loading && !loadError"
          type="button"
          @click="submit"
          :disabled="saving"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
        >{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { XMarkIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import ToggleSwitch from './ToggleSwitch.vue'

// startExpanded: lets the tour show the Availability editor (with its "Add block" link)
// pre-expanded — the Edit/Done toggle below is a real toggle, so simulating a click on
// it isn't safe (see the convention against that in tourSteps.js); this is a normal
// prop instead, set true only via App.vue's tour-driven tourExpandAvailability ref.
const props = defineProps({ startExpanded: { type: Boolean, default: false } })
const emit = defineEmits(['close'])
const { fetchApi } = useApi()

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const loading = ref(true)
const loadError = ref('')
const saving = ref(false)
const saveError = ref('')
const saveSuccess = ref(false)

const form = ref({ availability: {}, bio: '', cancellation_policy: '' })
const instrumentsText = ref('')
const editingAvailability = ref(props.startExpanded)

const activeDays = computed(() => WEEKDAYS.filter(day => form.value.availability[day]))

const fmtTime = t => {
  const [h, m] = t.split(':').map(Number)
  const ampm = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 || 12
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

const formatWindows = blocks => blocks.map(w => `${fmtTime(w.start)}–${fmtTime(w.end)}`).join(', ')

const toggleDay = (day, checked) => {
  if (checked) {
    form.value.availability[day] = [{ start: '15:00', end: '20:00' }]
  } else {
    delete form.value.availability[day]
  }
}

const addBlock = (day) => {
  const blocks = form.value.availability[day]
  const last = blocks[blocks.length - 1]
  blocks.push({ start: last?.end || '15:00', end: '20:00' })
}

const removeBlock = (day, i) => {
  form.value.availability[day].splice(i, 1)
}

onMounted(async () => {
  try {
    const data = await fetchApi('/api/teacher-availability/me')
    form.value.availability = data.availability || {}
    form.value.bio = data.bio || ''
    form.value.cancellation_policy = data.cancellation_policy || ''
    instrumentsText.value = (data.instruments || []).join(', ')
  } catch (e) {
    loadError.value = e.detail || 'Could not load your profile.'
  } finally {
    loading.value = false
  }
})

const submit = async () => {
  saveError.value = ''
  saving.value = true
  try {
    await fetchApi('/api/teacher-availability/me', {
      method: 'PUT',
      body: JSON.stringify({
        availability: form.value.availability,
        instruments: instrumentsText.value.split(',').map(s => s.trim()).filter(Boolean),
        bio: form.value.bio,
        cancellation_policy: form.value.cancellation_policy,
      }),
    })
    saveSuccess.value = true
    setTimeout(() => emit('close'), 1000)
  } catch (e) {
    saveError.value = e.detail || 'Something went wrong.'
  } finally {
    saving.value = false
  }
}
</script>
